import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { usePlaceMenu } from '@/hooks/usePlaceMenu';
import { lockAppScroll, unlockAppScroll } from '@/lib/scrollLock';
import type { MenuStatus, PlaceMenu } from '@shared/types';
import { CloseIcon } from './Icons';
import { PlaceAvatar } from './PlaceAvatar';

interface MenuSheetProps {
  slug: string;
  name: string;
  /** Drives the avatar glyph. Optional: the sheet only opens for food. */
  category?: string | null;
  open: boolean;
  onClose: () => void;
  /**
   * Show this menu instead of GET /places/:slug/menu. Used when the outlet
   * is not a Places row (Babcock National dining).
   */
  preset?: PlaceMenu;
  /**
   * `national` adds the header X and grabber swipe used on Babcock National.
   * Places keeps the text Close button only.
   */
  chrome?: 'places' | 'national';
  /**
   * Visible label of the primary link. Places and a real menu keep
   * "Open full menu". An outlet with no published menu passes an honest label.
   */
  linkLabel?: string;
  /**
   * Replaces the "read N days ago" age. Used for a dated preset PDF.
   * Places sheets omit this and keep the age.
   */
  datedLabel?: string;
}

/** Human age of the menu read, so stale prices are visibly stale. */
function agoLabel(iso: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z').getTime();
  if (!Number.isFinite(then)) return null;
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'read today';
  if (days === 1) return 'read yesterday';
  if (days < 30) return `read ${days} days ago`;
  const months = Math.round(days / 30);
  return months <= 1 ? 'read about a month ago' : `read about ${months} months ago`;
}

/**
 * Copy for the states where we have no items. These are deliberately
 * distinct: a place whose menu is a PDF is a different situation from one
 * that publishes nothing, and the user can act on the difference.
 */
function emptyCopy(status: MenuStatus, note: string | null): { title: string; body: string } {
  switch (status) {
    case 'image_only':
      return {
        title: 'Menu is published as an image',
        body: note ?? 'This restaurant posts its menu as a picture or PDF, so the items cannot be listed here.',
      };
    case 'blocked':
      return {
        title: 'Menu could not be read',
        body: note ?? 'This restaurant\u2019s site blocked automated reading, so the items could not be listed here.',
      };
    case 'not_published':
      return {
        title: 'No menu published',
        body: note ?? 'This restaurant does not publish an itemised menu online.',
      };
    default:
      return {
        title: 'Menu not collected yet',
        body: 'We haven\u2019t read this restaurant\u2019s menu yet. The link below goes to their own page.',
      };
  }
}

/**
 * Bottom-sheet menu popup. Mirrors ActionMenu's mechanics (portal, Escape,
 * backdrop tap, body scroll lock, deferred unmount for the close animation)
 * so the two sheets feel identical.
 *
 * Every price shown here is the exact string the restaurant printed — see
 * shared/types MenuItem. The sheet always shows where the data came from and
 * how old it is, because a menu price the app is confident about but wrong
 * about is worse than one the user knows to double-check.
 */
export function MenuSheet({
  slug,
  name,
  category,
  open,
  onClose,
  preset,
  chrome = 'places',
  linkLabel,
  datedLabel,
}: MenuSheetProps) {
  const [mounted, setMounted] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  /** True once the open animation must release `transform` so a drag can own it. */
  const [settled, setSettled] = useState(false);
  const dragOrigin = useRef<number | null>(null);
  const dragYRef = useRef(0);
  const [seenOpen, setSeenOpen] = useState(open);
  if (open !== seenOpen) {
    setSeenOpen(open);
    if (open) {
      // Reset before paint. DiningBody keeps this sheet mounted, and an
      // effect would run too late: the next open would first paint
      // menu-sheet--ready (no slide), then drop that class and replay
      // the slide-up from off-screen.
      setDragY(0);
      dragYRef.current = 0;
      setDragging(false);
      setSettled(false);
    }
  }
  const remote = usePlaceMenu(preset ? null : slug, !preset && (open || mounted));
  const menu = preset ?? remote.menu;
  const loading = preset ? false : remote.loading;
  const error = preset ? false : remote.error;
  const national = chrome === 'national';

  function onGrabDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (!national) return;
    dragOrigin.current = event.clientY;
    setDragging(true);
    // Drop the slide-up fill immediately. It holds translateY(0) and would
    // otherwise beat the drag offset for the whole gesture.
    setSettled(true);
    const target = event.currentTarget;
    if (typeof target.setPointerCapture === 'function') {
      try {
        target.setPointerCapture(event.pointerId);
      } catch {
        // jsdom has no pointer capture. The grabber still receives the test events.
      }
    }
  }

  function onGrabMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (dragOrigin.current == null) return;
    const next = Math.max(0, event.clientY - dragOrigin.current);
    dragYRef.current = next;
    setDragY(next);
  }

  function onGrabUp() {
    if (dragOrigin.current == null) return;
    const dragged = dragYRef.current;
    dragOrigin.current = null;
    setDragging(false);
    if (dragged > 72) {
      // Keep --sheet-drag at the release point. Zeroing it first paints the
      // sheet back at the top, then the close animation runs from there.
      onClose();
      return;
    }
    dragYRef.current = 0;
    setDragY(0);
  }

  useEffect(() => {
    if (!open) return;
    const settle = window.setTimeout(() => setSettled(true), 220);
    return () => window.clearTimeout(settle);
  }, [open]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      lockAppScroll();
      return () => {
        window.removeEventListener('keydown', onKey);
        unlockAppScroll();
      };
    }
    const t = window.setTimeout(() => setMounted(false), 200);
    return () => window.clearTimeout(t);
  }, [open, onClose]);

  if (!mounted && !open) return null;

  const hasItems = (menu?.itemCount ?? 0) > 0;
  const ago = agoLabel(menu?.fetchedAt ?? null);

  return createPortal(
    <div
      className={`action-menu ${open ? 'action-menu--open' : 'action-menu--closing'}`}
      role="dialog"
      aria-modal="true"
      aria-label={`Menu for ${name}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`action-menu__sheet menu-sheet${national ? ' menu-sheet--national' : ''}${
          national && (settled || dragging) ? ' menu-sheet--ready' : ''
        }${national && dragging ? ' menu-sheet--dragging' : ''}`}
        style={national ? ({ '--sheet-drag': `${dragY}px` } as CSSProperties) : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="action-menu__grabber"
          aria-hidden
          onPointerDown={onGrabDown}
          onPointerMove={onGrabMove}
          onPointerUp={onGrabUp}
          onPointerCancel={onGrabUp}
        />

        <div className="menu-sheet__head">
          <PlaceAvatar name={name} slug={slug} category={category ?? 'food'} size={40} />
          <div className="menu-sheet__head-text">
            <div className="menu-sheet__title">{name}</div>
            <div className="menu-sheet__sub">
              {loading && 'Loading menu\u2026'}
              {!loading && hasItems && (
                <>
                  {menu?.itemCount} items
                  {datedLabel ? ` \u00b7 ${datedLabel}` : ago ? ` \u00b7 ${ago}` : ''}
                </>
              )}
              {!loading && !hasItems && !error && 'Menu'}
              {error && 'Menu unavailable'}
            </div>
          </div>
          {national && (
            <button type="button" className="menu-sheet__x" onClick={onClose} aria-label="Close">
              <CloseIcon width={22} height={22} />
            </button>
          )}
        </div>

        {loading && (
          <div className="menu-sheet__body">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8 }} />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="menu-sheet__empty">
            <p className="menu-sheet__empty-title">{'Couldn\u2019t load the menu'}</p>
            <p className="menu-sheet__empty-body">Check your connection and try again.</p>
          </div>
        )}

        {!loading && !error && hasItems && menu?.note && (
          <p className="menu-sheet__sourcenote">{menu.note}</p>
        )}

        {!loading && !error && hasItems && (
          <div className="menu-sheet__body">
            {menu!.sections.map((sec, si) => (
              <section key={`${sec.section ?? 'none'}-${si}`} className="menu-sec">
                {sec.section && <h3 className="menu-sec__label">{sec.section}</h3>}
                {sec.items.map((it) => (
                  <div key={it.id} className="menu-item">
                    <div className="menu-item__main">
                      <span className="menu-item__name">{it.name}</span>
                      {it.description && <span className="menu-item__desc">{it.description}</span>}
                    </div>
                    {it.price && <span className="menu-item__price">{it.price}</span>}
                  </div>
                ))}
              </section>
            ))}
            {/* Jersey Mike's and other chains publish no prices without a store
                selection. Say so rather than letting the user assume we lost them. */}
            {menu!.sections.every((s) => s.items.every((i) => !i.price)) && (
              <p className="menu-sheet__pricenote">
                {'This restaurant doesn\u2019t publish prices online \u2014 open their full menu for current pricing.'}
              </p>
            )}
          </div>
        )}

        {!loading && !error && !hasItems && (
          <div className="menu-sheet__empty">
            <p className="menu-sheet__empty-title">{emptyCopy(menu?.status ?? 'ok', menu?.note ?? null).title}</p>
            <p className="menu-sheet__empty-body">{emptyCopy(menu?.status ?? 'ok', menu?.note ?? null).body}</p>
          </div>
        )}

        {menu?.menuUrl && (
          <a className="menu-sheet__link" href={menu.menuUrl} target="_blank" rel="noreferrer">
            {linkLabel ?? 'Open full menu'}
          </a>
        )}

        <button type="button" className="action-menu__close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
}
